import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Clock, Plus, Trash2, Save, Loader2 } from "lucide-react";
import { next7Days, dateKey, fmtTime } from "@/components/store/PickupHoursCard";

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const DayEditor = ({ date, entry, onSaved }) => {
  const key = dateKey(date);
  const [closed, setClosed] = useState(!!entry?.closed);
  const [windows, setWindows] = useState(
    entry?.windows?.length ? entry.windows.map((w) => ({ ...w })) : [{ start: "", end: "" }]
  );
  const [saving, setSaving] = useState(false);

  const setWin = (i, field, val) => setWindows((prev) => prev.map((w, k) => (k === i ? { ...w, [field]: val } : w)));
  const addWin = () => setWindows((prev) => [...prev, { start: "", end: "" }]);
  const removeWin = (i) => setWindows((prev) => prev.filter((_, k) => k !== i));

  const save = async () => {
    setSaving(true);
    try {
      const payload = closed
        ? { closed: true, windows: [] }
        : { closed: false, windows: windows.filter((w) => w.start && w.end) };
      const r = await api.put(`/admin/pickup-hours/${key}`, payload);
      toast.success("Saved");
      onSaved && onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
    setSaving(false);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`pickup-editor-${key}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-heading text-brand-navy">
          {WEEKDAYS[date.getDay()]} · {MONTHS[date.getMonth()]} {date.getDate()}
        </div>
        <label className="inline-flex items-center gap-2 text-sm font-bold">
          <input type="checkbox" checked={closed} onChange={(e) => setClosed(e.target.checked)} data-testid={`pickup-closed-${key}`} />
          Closed this day
        </label>
      </div>

      {!closed && (
        <div className="mt-4 space-y-2">
          {windows.map((w, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`pickup-window-${key}-${i}`}>
              <input type="time" value={w.start} onChange={(e) => setWin(i, "start", e.target.value)} data-testid={`pickup-start-${key}-${i}`}
                className="h-10 rounded-lg border border-slate-200 px-2" />
              <span className="text-slate-400">to</span>
              <input type="time" value={w.end} onChange={(e) => setWin(i, "end", e.target.value)} data-testid={`pickup-end-${key}-${i}`}
                className="h-10 rounded-lg border border-slate-200 px-2" />
              <span className="text-xs text-slate-400 hidden sm:inline">{w.start && w.end ? `${fmtTime(w.start)} – ${fmtTime(w.end)}` : ""}</span>
              {windows.length > 1 && (
                <button onClick={() => removeWin(i)} data-testid={`pickup-window-remove-${key}-${i}`} className="text-brand-red p-1.5 hover:bg-red-50 rounded"><Trash2 className="h-4 w-4" /></button>
              )}
            </div>
          ))}
          <button onClick={addWin} data-testid={`pickup-add-window-${key}`} className="inline-flex items-center gap-1 text-sm font-bold text-brand-navy hover:text-brand-red">
            <Plus className="h-4 w-4" /> Add time window
          </button>
        </div>
      )}

      <button onClick={save} disabled={saving} data-testid={`pickup-save-${key}`}
        className="mt-4 inline-flex items-center gap-2 bg-brand-navy text-white rounded-full h-10 px-5 font-bold active:scale-95 disabled:opacity-60">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save day
      </button>
    </div>
  );
};

export const PickupHoursTab = () => {
  const [hours, setHours] = useState({});
  const [loading, setLoading] = useState(true);
  const days = next7Days();

  useEffect(() => {
    api.get("/settings").then((r) => setHours(r.data.pickup_hours || {})).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-slate-400">Loading…</div>;

  return (
    <div data-testid="pickup-hours-tab">
      <div className="flex items-center gap-2 mb-1">
        <Clock className="h-6 w-6 text-brand-red" />
        <div className="font-heading text-2xl text-brand-navy">Pickup Hours</div>
      </div>
      <p className="text-slate-500 text-sm mb-5">
        Set the storage-unit pickup windows for the coming 7 days. Mark a day Closed, or add one or
        more time windows (e.g. morning + evening). These show on the home page. Past days drop off
        automatically.
      </p>
      <div className="grid md:grid-cols-2 gap-4">
        {days.map((d) => {
          const key = dateKey(d);
          return (
            <DayEditor
              key={key}
              date={d}
              entry={hours[key]}
              onSaved={(settings) => setHours(settings.pickup_hours || {})}
            />
          );
        })}
      </div>
    </div>
  );
};
